export interface DurationRangeItem {
  startTime: number; // 毫秒时间戳
  endTime?: number;  // 毫秒时间戳
  operatorId?: number; // 审核人ID（用于区分不同审核人产生的分段）
  suspendReason?: string; // 暂存原因
}

export interface OrderAuditEntity {
  id?: number;
  status?: number; // 审核状态：1正在审核/2挂起/3成功/4失败
  orderNo?: string; // 关联订单号
  storeId?: number; // 店铺ID
  auditId?: number; // 审核员ID
  durationTime?: number; // 累计审核时长(秒)
  durationRange?: DurationRangeItem[]; // 审核时间段记录

  // 欺诈审核项
  isCashOutSuspected?: boolean; // 是否疑似套现
  suspectedCashOutRemark?: string; // 套现说明
  isMachineSuspected?: boolean; // 是否疑似撸机
  suspectedMachineRemark?: string; // 撸机说明
  isProxyOrderSuspected?: boolean; // 是否疑似代下单
  suspectedProxyOrderRemark?: string; // 代下单说明

  // 身份与资质
  occupation?: string; // 职业
  occupationOther?: string; // 其他职业说明
  monthlyLivingExpense?: number; // 月生活费(学生)
  expenseTransferRecords?: string[]; // 生活费转账记录图片
  companyName?: string; // 工作单位(上班族)
  workYears?: string; // 工作年限
  monthlyIncome?: number; // 月收入
  incomeProofDocs?: string[]; // 收入证明材料

  // 家庭与信用
  maritalStatus?: number; // 婚姻状况
  hasChildren?: boolean; // 是否有子女
  childrenCount?: number; // 子女数量
  supportElderly?: boolean; // 需抚养老人
  elderlyCount?: number; // 老人数量
  otherDebtAmount?: number; // 其他贷款总额
  sesameCreditScore?: number; // 芝麻分
  wechatPayScore?: number; // 微信分
  creditReportStatus?: number; // 征信记录
  rentalHistoryStatus?: number; // 租赁记录
  uploadOtherProofMaterials?: string[]; // 上传其他证明材料
  otherRemarks?: string; // 其他备注

  createdAt?: string;
  updatedAt?: string;
  finishedAt?: string;
}

export interface OrderAuditContactEntity {
  id?: number;
  orderNo?: string;
  reviewId?: number;
  contactName?: string;
  relationship?: string;
  phoneNumber?: string;
  contactProofMaterials?: string[]; // 证明材料
  sortOrder?: number;
  verificationStatus?: number; // 核实情况：1正常 2异常 3未接通
  remark?: string; // 备注
  createdAt?: string;
  updatedAt?: string;
}

export const AuditStatusMap: Record<number, string> = {
  1: '正在审核',
  2: '挂起',
  3: '建议通过',
  4: '建议拒绝',
  5: '已释放',
};

export type DurationRangeValueKey = 'name' | 'startTime' | 'endTime' | 'duration' | 'suspendReason';
export type DurationRangeSelect =
  | 'all'
  | 'last'
  | number
  | {
    operatorId?: number;
    index?: number;
  };

export const pickDurationRange = (
  ranges: DurationRangeItem[] | undefined,
  key: DurationRangeValueKey,
  select?: DurationRangeSelect
): any => {
  const list = Array.isArray(ranges) ? ranges.filter((r) => r && typeof r.startTime === 'number') : [];
  if (list.length === 0) {
    const emptyValues: Record<DurationRangeValueKey, any> = {
      name: select === 'all' ? [] : undefined,
      startTime: undefined,
      endTime: undefined,
      duration: 0,
      suspendReason: undefined,
    };
    return emptyValues[key];
  }

  const byOperator = (opId?: number) =>
    list.filter((r) => (typeof opId === 'number' && Number.isFinite(opId) ? r.operatorId === opId : true));
  const last = list[list.length - 1];
  const index = typeof select === 'number' ? select : (select as any)?.index;
  const operatorId = (select as any)?.operatorId;

  const toDuration = (r: DurationRangeItem) => {
    const s = typeof r.startTime === 'number' ? r.startTime : undefined;
    const e = typeof r.endTime === 'number' ? r.endTime : undefined;
    if (!s || !e) return 0;
    const diff = Math.max(0, Math.floor((e - s) / 1000));
    return diff;
  };

  const selectByIndex = (getter: (item: DurationRangeItem) => any) => {
    if (typeof index !== 'number' || !Number.isFinite(index)) return undefined;
    const item = list[index];
    if (!item) return undefined;
    return getter(item);
  };

  const selectByOperator = (getter: (items: DurationRangeItem[]) => any) => {
    if (typeof operatorId !== 'number' || !Number.isFinite(operatorId)) return undefined;
    const pool = byOperator(operatorId);
    if (pool.length === 0) return undefined;
    return getter(pool);
  };

  const handlers: Record<DurationRangeValueKey, () => any> = {
    name: () => {
      if (select === 'all') {
        return Array.from(new Set(list.map((r) => r.operatorId).filter((v): v is number => typeof v === 'number')));
      }
      if (select === 'last') {
        return typeof last?.operatorId === 'number' ? last.operatorId : undefined;
      }
      const byIndex = selectByIndex((item) => item.operatorId);
      if (byIndex !== undefined) return typeof byIndex === 'number' ? byIndex : undefined;
      if (typeof operatorId === 'number' && Number.isFinite(operatorId)) return operatorId;
      return typeof list[0]?.operatorId === 'number' ? list[0].operatorId : undefined;
    },
    startTime: () => {
      if (select === 'all') {
        const min = Math.min(...list.map((r) => r.startTime as number));
        return Number.isFinite(min) ? min : undefined;
      }
      if (select === 'last') {
        return typeof last?.startTime === 'number' ? last.startTime : undefined;
      }
      const byIndex = selectByIndex((item) => item.startTime);
      if (byIndex !== undefined) return typeof byIndex === 'number' ? byIndex : undefined;
      return selectByOperator((pool) => {
        const min = Math.min(...pool.map((r) => r.startTime as number));
        return Number.isFinite(min) ? min : undefined;
      });
    },
    endTime: () => {
      if (select === 'all') {
        const values = list.map((r) => r.endTime).filter((v): v is number => typeof v === 'number');
        if (values.length === 0) return undefined;
        const max = Math.max(...values);
        return Number.isFinite(max) ? max : undefined;
      }
      if (select === 'last') {
        return typeof last?.endTime === 'number' ? last.endTime : undefined;
      }
      const byIndex = selectByIndex((item) => item.endTime);
      if (byIndex !== undefined) return typeof byIndex === 'number' ? byIndex : undefined;
      return selectByOperator((pool) => {
        const values = pool.map((r) => r.endTime).filter((v): v is number => typeof v === 'number');
        if (values.length === 0) return undefined;
        const max = Math.max(...values);
        return Number.isFinite(max) ? max : undefined;
      });
    },
    duration: () => {
      if (select === 'all') {
        return list.reduce((sum, r) => sum + toDuration(r), 0);
      }
      if (select === 'last') {
        return toDuration(last);
      }
      const byIndex = selectByIndex((item) => toDuration(item));
      if (byIndex !== undefined) return byIndex;
      return byOperator(operatorId).reduce((sum, r) => sum + toDuration(r), 0);
    },
    suspendReason: () => {
      if (select === 'last') {
        return last?.suspendReason;
      }
      return undefined;
    },
  };

  return handlers[key]?.();
};
